import { defineStore } from "pinia";
import axios from "axios";
import { API_URL } from "@/config/api";

let activeGenerationController = null;

const safeAiTripError = (error, fallback) => {
  const status = error.response?.status;
  if (status === 401) return "ログインの有効期限が切れました。もう一度ログインしてください。";
  if (status === 403) return "この操作を行う権限がありません。";
  if (status === 404) return "対象の旅程が見つかりませんでした。";
  return fallback;
};

export const cancelAiTripGeneration = () => {
  activeGenerationController?.abort();
};

export const useAiTripStore = defineStore("aiTripPlanning", {
  state: () => ({
    isGenerating: false,
    generationTask: null,
    isSaving: false,
    error: null,
    proposals: null,
    dailyOptions: null,
    preview: null,
    lastResult: null,
  }),
  actions: {
    beginGeneration(task) {
      activeGenerationController?.abort();
      activeGenerationController = new AbortController();
      this.isGenerating = true;
      this.generationTask = task;
      this.error = null;
      return activeGenerationController;
    },
    finishGeneration(controller) {
      if (activeGenerationController === controller) {
        activeGenerationController = null;
        this.isGenerating = false;
        this.generationTask = null;
      }
    },
    cancelGeneration() {
      cancelAiTripGeneration();
    },
    async generateProposals(conditions) {
      const controller = this.beginGeneration("proposals");
      try {
        const response = await axios.post(
          `${API_URL}/ai-trips/proposals`,
          conditions,
          { timeout: 120000, signal: controller.signal },
        );
        this.proposals = response.data;
        this.dailyOptions = null;
        this.preview = null;
        return response.data;
      } catch (error) {
        if (axios.isCancel(error)) throw error;
        this.error = safeAiTripError(error, "旅程の作成に失敗しました。時間をおいて再度お試しください。");
        throw error;
      } finally {
        this.finishGeneration(controller);
      }
    },
    async generateDailyOptions(conditions, flowOption) {
      const controller = this.beginGeneration("daily-options");
      try {
        const response = await axios.post(
          `${API_URL}/ai-trips/daily-options`,
          {
            conditions,
            flowSelection: { flowOption },
          },
          { timeout: 120000, signal: controller.signal },
        );
        this.dailyOptions = response.data;
        this.preview = null;
        return response.data;
      } catch (error) {
        if (axios.isCancel(error)) throw error;
        this.error = safeAiTripError(error, "食事・ホテル候補の作成に失敗しました。時間をおいて再度お試しください。");
        throw error;
      } finally {
        this.finishGeneration(controller);
      }
    },
    async regenerateDailyOptions(conditions, flowOption, targets) {
      const controller = this.beginGeneration("regenerate-options");
      try {
        let replacements;
        let message;
        try {
          const response = await axios.post(
            `${API_URL}/ai-trips/daily-options/regenerate`,
            {
              conditions,
              flowSelection: { flowOption },
              targets,
            },
            { timeout: 120000, signal: controller.signal },
          );
          replacements = response.data.replacements;
          message = response.data.message;
        } catch (error) {
          if (axios.isCancel(error)) throw error;
          if (process.env.VUE_APP_AI_TRIP_ALLOW_FULL_REGEN_FALLBACK !== "true") {
            throw error;
          }
          const fallbackResponse = await axios.post(
            `${API_URL}/ai-trips/daily-options`,
            {
              conditions,
              flowSelection: { flowOption },
            },
            { timeout: 120000, signal: controller.signal },
          );
          const optionKeys = {
            lunchOption: "lunchOptions",
            dinnerOption: "dinnerOptions",
            hotelOption: "hotelOptions",
          };
          replacements = targets.map((target) => {
            const day = fallbackResponse.data.days.find(
              (item) => item.date === target.date,
            );
            return {
              date: target.date,
              optionType: target.optionType,
              options: day?.[optionKeys[target.optionType]] || [],
            };
          });
          if (replacements.some((replacement) => replacement.options.length !== 3)) {
            throw error;
          }
          message = "選択した候補を新しい3案に差し替えました。";
        }
        const optionKeys = {
          lunchOption: "lunchOptions",
          dinnerOption: "dinnerOptions",
          hotelOption: "hotelOptions",
        };
        replacements.forEach((replacement) => {
          const day = this.dailyOptions?.days.find(
            (item) => item.date === replacement.date,
          );
          if (day) {
            day[optionKeys[replacement.optionType]] = replacement.options;
          }
        });
        this.dailyOptions.message = message;
        this.preview = null;
        return { message, replacements };
      } catch (error) {
        if (axios.isCancel(error)) throw error;
        this.error = safeAiTripError(error, "候補の再提案に失敗しました。時間をおいて再度お試しください。");
        throw error;
      } finally {
        this.finishGeneration(controller);
      }
    },
    async generatePreview(conditions, selections) {
      const controller = this.beginGeneration("preview");
      try {
        const response = await axios.post(
          `${API_URL}/ai-trips/preview`,
          { conditions, selections },
          { timeout: 120000, signal: controller.signal },
        );
        this.preview = response.data;
        return response.data;
      } catch (error) {
        if (axios.isCancel(error)) throw error;
        this.error = safeAiTripError(error, "詳細旅程の作成に失敗しました。時間をおいて再度お試しください。");
        throw error;
      } finally {
        this.finishGeneration(controller);
      }
    },
    async saveTrip(conditions, itinerary, preservedOptions = []) {
      this.isSaving = true;
      this.error = null;
      try {
        const response = await axios.post(`${API_URL}/ai-trips/save`, {
          conditions,
          itinerary,
          preservedOptions,
        });
        this.lastResult = response.data;
        return response.data;
      } catch (error) {
        this.error = safeAiTripError(error, "しおりの保存に失敗しました。時間をおいて再度お試しください。");
        throw error;
      } finally {
        this.isSaving = false;
      }
    },
    reset() {
      cancelAiTripGeneration();
      this.isGenerating = false;
      this.generationTask = null;
      this.error = null;
      this.proposals = null;
      this.dailyOptions = null;
      this.preview = null;
      this.lastResult = null;
    },
  },
});
